import { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Search,
  Filter,
  Activity,
  FileText,
  Package,
  AlertTriangle,
  Archive,
  Trash2,
  Download,
  FileSpreadsheet,
  Upload,
  Scan as ScanIcon,
  RefreshCw,
  FolderOpen,
  Check,
  X,
  ArchiveRestore,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  User,
  Clock,
  Calendar,
  Component
} from "lucide-react";
import { UnifiedCard } from "../components/ui/unified-card";
import { ArchiveScan } from "../components/ArchiveScan";
import { DeleteScan } from "../components/DeleteScan";
import { RestoreScan } from "../components/RestoreScan";
import { NewScan } from "../components/NewScan";
import { UploadOrtFiles } from "../components/UploadOrtFiles";
import { useScans } from "../hooks/useScans";
import { FindScansOptions, Scan, ScanAPI, ScanStatus, UpdateScanData } from "../api/scans";
import { Paginator } from "@/components/Paginator";
import { getScanTypeColor, getStatusColor } from "@/helpers/common";
import { getResultFormatBadge } from "@/helpers/scanResultUpload";
import { MultiSelect } from "@/components/ui/multi-select";
import { useApplicationList } from "@/hooks/useApplications";
import { useProjectList } from "@/hooks/useProjects";
import { toast } from "sonner";
import { useHasPermission } from "@/hooks/useHasPermission";
import { useEntitlement } from "@/hooks/useEntitlement";
import type { ListCardAction } from "../components/ui/list-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';


interface ScansProps {
  isGridLayout?: boolean;
  onNavigate?: (route: string, params?: any) => void;
  workgroupId: number;
  applicationId?: number;
  projectId?: number;
}

const statusLabels = {
  [ScanStatus.PENDING]: 'Pending',
  [ScanStatus.RUNNING]: 'Running',
  [ScanStatus.COMPLETED]: 'Completed',
  [ScanStatus.FAILED]: 'Failed',
  [ScanStatus.CANCELLED]: 'Cancelled',
  [ScanStatus.CONFIGURED]: 'Configured'
};

// scan.repourl reflects the PARENT PROJECT's current repo, not what this scan actually ran
// against -- it changes retroactively if the project is later reconnected/edited. Prefer the
// server-stamped scanorigin (immune to that drift), then the per-scan scanparameters.scanMethod
// recorded client-side at creation time, and only fall back to repourl truthiness for scans that
// predate both fields.
function getScanOrigin(scan: { scanorigin?: string; scanparameters?: any; repourl?: string }): 'github' | 'manual' {
  return (scan.scanorigin as 'github' | 'manual') ?? scan.scanparameters?.scanMethod ?? (scan.repourl ? 'github' : 'manual');
}

export function Scans({
  isGridLayout = true,
  workgroupId,
  applicationId: propApplicationId,
  projectId: propProjectId,
  onNavigate,
}: ScansProps) {
  const canCreate = useHasPermission('scans', 'create');
  const canDelete = useHasPermission('scans', 'delete');
  const canRestore = useHasPermission('scans', 'restore');
  const canExport = useHasPermission('scans', 'export');
  const canExportScanReport = useEntitlement('scans', 'export');
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();
  const [searchParams] = useSearchParams();
  const queryApplicationId = searchParams.get('applicationId');
  const queryProjectId = searchParams.get('projectId');
  const projectId = propProjectId || (urlProjectId ? parseInt(urlProjectId) : (queryProjectId ? parseInt(queryProjectId) : undefined));
  const applicationId = propApplicationId || (queryApplicationId ? parseInt(queryApplicationId) : undefined);

  useEffect(() => {
    setSearchTerm('');
    setSelectedApplications([]);
    setSelectedProjects([]);
    setCurrentPage(1);
    setStatusFilter('all');
    setFavouriteFilter('all');
    setProgressFilter('all');
    setSortBy('createdon');
    setSortOrder('desc');
  }, [projectId,applicationId, workgroupId]);

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rescanModalOpen, setRescanModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [selectedScan, setSelectedScan] = useState<Scan | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedApplications, setSelectedApplications] = useState<(string | number)[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<(string | number)[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [favouriteFilter, setFavouriteFilter] = useState<string>('all');
  const [progressFilter, setProgressFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdon');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data: allScans, loading, error, refetch } = useScans({
      workgroupId,
      projectId,
      applicationId,
  } as FindScansOptions);
  const getSortDisplayTitle = (sortKey: string) => {
    const sortTitles: Record<string, string> = {
      'createdon': 'Created Date',
      'startedate': 'Start Date',
      'completedate': 'Completion Date',
      'componentsfound': 'Components Found',
      'vulnerabilitiesfound': 'Vulnerabilities Found',
      'licenseissuesfound': 'License Issues',
      'overallscore': 'Overall Score'
    };
    return sortTitles[sortKey] || sortKey;
  };

  const {
    filteredScans,
    paginatedScans,
  } = useMemo(() => {
    const filtered = allScans?.filter(scan => {
      const searchMatch = searchTerm === '' ||
        scan.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan.version?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan.scantype?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan.scantype?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scan.application_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const applicationMatch = selectedApplications.length === 0 ||
        selectedApplications.includes(scan.applicationid) ||
        (scan.applicationid && selectedApplications.includes(scan.applicationid));

      const projectMatch = selectedProjects.length === 0 ||
        selectedProjects.includes(scan.projectid) ||
        (scan.projectid && selectedProjects.includes(scan.projectid));

      const statusMatch = statusFilter === 'all' ||
        (statusFilter === 'Active' && scan.isactive) ||
        (statusFilter === 'Archived' && !scan.isactive);

      const progressMatch = progressFilter === 'all' || scan.scanstatus === progressFilter;

      return searchMatch && applicationMatch && projectMatch && statusMatch && progressMatch;
    }) || [];

    const sorted = [...filtered].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'createdon':
          aValue = a.createdon;
          bValue = b.createdon;
          break;
        case 'startdate':
          aValue = a.startdate || a.createdon;
          bValue = b.startdate || b.createdon;
          break;
        case 'completedate':
          aValue = a.completedate || a.createdon;
          bValue = b.completedate || b.createdon;
          break;
        case 'progresspercentage':
          aValue = a.progresspercentage || 0;
          bValue = b.progresspercentage || 0;
          break;
        case 'filesscanned':
          aValue = a.filesscanned || 0;
          bValue = b.filesscanned || 0;
          break;
        case 'componentsfound':
          aValue = a.componentsfound || 0;
          bValue = b.componentsfound || 0;
          break;
        case 'vulnerabilitiesfound':
          aValue = a.vulnerabilitiesfound || 0;
          bValue = b.vulnerabilitiesfound || 0;
          break;
        case 'licenseissuesfound':
          aValue = a.licenseissuesfound || 0;
          bValue = b.licenseissuesfound || 0;
          break;
        case 'overallscore':
          aValue = a.overallscore || 0;
          bValue = b.overallscore || 0;
          break;
        default:
          aValue = a.createdon;
          bValue = b.createdon;
      }

      if (sortBy === 'createdon' || sortBy === 'startedat' || sortBy === 'completedat') {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        if (sortOrder === 'asc') {
          return dateA > dateB ? 1 : -1;
        } else {
          return dateA < dateB ? 1 : -1;
        }
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    const itemsPerPage = 9;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = sorted.slice(startIndex, endIndex);

    return {
      filteredScans: filtered,
      paginatedScans: paginated,
    };
  }, [
    allScans,
    searchTerm,
    selectedApplications,
    selectedProjects,
    statusFilter,
    progressFilter,
    currentPage,
    sortBy,
    sortOrder,
    projectId
  ]);

  const {
    data: applications,
    loading: applicationsLoading
  } = useApplicationList(workgroupId);

  const {
    data: projects,
    loading: projectsLoading
  } = useProjectList(workgroupId, applicationId);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleArchiveScan = (scan: Scan) => {
    setSelectedScan(scan);
    setArchiveModalOpen(true);
  };

  const handleDeleteScan = (scan: Scan) => {
    setSelectedScan(scan);
    setDeleteModalOpen(true);
  };

  const handleRestoreScan = (scan: Scan) => {
    setSelectedScan(scan);
    setRestoreModalOpen(true);
  };

  const handleRescan = (scan: Scan) => {
    setSelectedScan(scan);
    setRescanModalOpen(true);
  };

  const handleDownloadOrt = async (scan: Scan) => {
    const loadingToast = toast.loading('Preparing ORT files...');
    try {
      await ScanAPI.downloadOrtReport(scan.id);
      toast.dismiss(loadingToast);
      toast.success('ORT report downloaded successfully');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to download ORT report. ' + (error instanceof Error ? error.message : 'Please try again.'));
    }
  };

  const handleExportScanReport = async (scan: Scan) => {
    const loadingToast = toast.loading('Generating Scan report... This may take a few moments.');
    try {
      await ScanAPI.downloadSccReport(scan.id);
      toast.dismiss(loadingToast);
      toast.success('Scan report downloaded successfully');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to download Scan report. Please try again.');
    }
  };

  const handleUploadOrt = (scan: Scan) => {
    setSelectedScan(scan);
    setUploadModalOpen(true);
  };

  const handleRefreshStatus = async (scan: Scan) => {
    try {
      toast.info(`Refreshing status for scan #${scan.code}...`);

      const ortStatusResponse = await ScanAPI.getOrtScanStatus(scan.id);

      if (ortStatusResponse.code === '200' && ortStatusResponse.data) {
        const ortStatus = ortStatusResponse.data;

        let newStatus: string = scan.scanstatus;
        let newProgress: number = scan.progresspercentage || 0;
        let errorMessage: string = '';

        if (ortStatus.status === 'completed') {
          if (ortStatus.errorMessage) {
            newStatus = ScanStatus.FAILED;
            errorMessage = ortStatus.errorMessage;
          } else {
            newStatus = ScanStatus.COMPLETED;
            newProgress = 100;
          }
        } else if (ortStatus.status === 'in_progress') {
          newStatus = ScanStatus.RUNNING;
          newProgress = ortStatus.progress || Math.max(newProgress, 25);
        } else if (ortStatus.status === 'queued' || ortStatus.status === 'requested' || ortStatus.status === 'waiting') {
          newStatus = ScanStatus.PENDING;
          newProgress = Math.max(newProgress, 5);
        } else if (ortStatus.status === 'cancelled') {
          newStatus = ScanStatus.CANCELLED;
          errorMessage = 'GitHub workflow was cancelled';
        } else if (ortStatus.status === 'failure') {
          newStatus = ScanStatus.FAILED;
          errorMessage = ortStatus.errorMessage || 'GitHub workflow failed';
        }

        if (newStatus !== scan.scanstatus || newProgress !== scan.progresspercentage) {


          const updateData: UpdateScanData = {
            status: newStatus,
            progresspercentage: newProgress,
            scanlog: `Status refreshed at ${new Date().toISOString()}: ${newStatus}${errorMessage ? ` - ${errorMessage}` : ''}`,

          };

          if (errorMessage) {
            updateData.errormessage = errorMessage;
          }


          if (newStatus === ScanStatus.COMPLETED || newStatus === ScanStatus.FAILED || newStatus === ScanStatus.CANCELLED) {
            updateData.completedate = new Date();
          }

          const updateResponse = await ScanAPI.update(scan.id, updateData);

          if (updateResponse.code === '200') {
            toast.success(`Scan #${scan.code} status updated to: ${newStatus}`);

            refetch();
          } else {
            toast.error('Failed to update scan status in database');
          }
        } else {
          toast.info(`Scan #${scan.code} status is already up to date: ${newStatus}`);
        }
      } else {


        refetch();
      }
    } catch (error) {
      toast.error(`Failed to refresh status for scan #${scan.code}. ${error instanceof Error ? error.message : 'Please try again.'}`);

      refetch();
    }
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const handleApplicationFilterChange = (applicationIds: (string | number)[]) => {
    setSelectedApplications(applicationIds);

    if (applicationIds.length > 0) {
      const filteredProjectIds = projects
        ?.filter(project => applicationIds.includes(project.applicationid))
        ?.map(project => project.id) || [];


      const validSelectedProjects = selectedProjects.filter(projectId =>
        filteredProjectIds.includes(Number(projectId))
      );

      if (validSelectedProjects.length !== selectedProjects.length) {
        setSelectedProjects(validSelectedProjects);
      }
    }

    setCurrentPage(1);
  };

  const handleProjectFilterChange = (projectIds: (string | number)[]) => {
    setSelectedProjects(projectIds);

    if (projectIds.length > 0) {
      const requiredApplicationIds = new Set<string | number>();

      projects?.forEach(project => {
        if (projectIds.includes(project.id) && project.applicationid) {
          requiredApplicationIds.add(project.applicationid);
        }
      });

      const newApplicationIds = Array.from(requiredApplicationIds);

      const mergedApplicationIds = Array.from(new Set([...selectedApplications, ...newApplicationIds]));

      if (mergedApplicationIds.length !== selectedApplications.length) {
        setSelectedApplications(mergedApplicationIds);
      }
    }

    setCurrentPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleFavouriteFilter = (filter: string) => {
    setFavouriteFilter(filter);
    setCurrentPage(1);
  };

  const handleProgressFilter = (progress: string) => {
    setProgressFilter(progress);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSelectedApplications([]);
    setSelectedProjects([]);
    setStatusFilter('all');
    setFavouriteFilter('all');
    setProgressFilter('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedApplications.length > 0 ||
    selectedProjects.length > 0 ||
    statusFilter !== 'all' ||
    favouriteFilter !== 'all' ||
    progressFilter !== 'all' ||
    searchTerm.length > 0;
  const applicationOptions = applications?.map(app => ({
    value: app.id,
    label: app.name
  })) || [];


  const projectOptions = useMemo(() => {
    if (!projects) return [];

    if (selectedApplications.length === 0) {
      return projects.map(project => {
        const appName = applications?.find(app => app.id === project.applicationid)?.name || 'Unknown App';
        return {
          value: project.id,
          label: `${project.name}`
        };
      });
    }

    return projects
      .filter(project => selectedApplications.includes(project.applicationid))
      .map(project => {
        const appName = applications?.find(app => app.id === project.applicationid)?.name || 'Unknown App';
        return {
          value: project.id,
          label: `${project.name}`
        };
      });
  }, [projects, selectedApplications, applications]);

  const ScanCard = ({ scan }: { scan: Scan }) => {
    // Clicking the card does the same as "View Details".
    const openScan = () => onNavigate('component-view-report/' + scan.id);
    const scanHref = `/component-view-report/${scan.id}`;

    const downloadReportAction: ListCardAction[] = canExport ? [{
      label: 'Download Report',
      icon: <Download className="w-4 h-4 mr-2" />,
      ...(scan.scanstatus === ScanStatus.COMPLETED
        ? {
            subActions: [
              {
                label: 'Download ORT Files',
                description: 'ORT result files (.zip)',
                icon: <Download className="w-4 h-4 mr-2 mt-0.5" />,
                onClick: () => handleDownloadOrt(scan)
              },
              {
                label: 'Export Scan Report',
                description: 'Excel report (.xlsx)',
                icon: <FileSpreadsheet className="w-4 h-4 mr-2 mt-0.5" />,
                onClick: () => handleExportScanReport(scan),
                disabled: !canExportScanReport,
                disabledReason: 'The comprehensive Excel scan report is available on the Professional plan and above.'
              }
            ]
          }
        : { disabled: true, disabledReason: 'Available when the scan completes.' })
    }] : [];

    // Archive / Restore / Delete sit below a divider; whichever of them shows first carries it.
    const lifecycleActions: ListCardAction[] = [
      ...(!scan.isactive
        ? (canRestore ? [{
            label: 'Restore Scan',
            icon: <ArchiveRestore className="w-4 h-4 mr-2" />,
            onClick: () => handleRestoreScan(scan),
            variant: 'default' as const
          }] : [])
        : (canDelete ? [{
            label: 'Archive Scan',
            icon: <Archive className="w-4 h-4 mr-2" />,
            onClick: () => handleArchiveScan(scan),
            variant: 'danger' as const
          }] : [])),
      ...(canDelete ? [{
        label: 'Delete Scan',
        icon: <Trash2 className="w-4 h-4 mr-2" />,
        onClick: () => handleDeleteScan(scan),
        variant: 'danger' as const
      }] : [])
    ].map((action, index) => (index === 0 ? { ...action, separatorBefore: true } : action));

    const actions: ListCardAction[] = [
      ...(scan.scanstatus === ScanStatus.RUNNING ? [{
        label: 'Refresh Status',
        icon: <RefreshCw className="w-4 h-4 mr-2" />,
        onClick: () => handleRefreshStatus(scan)
      }] : []),
      {
        label: 'View Details',
        icon: <FileText className="w-4 h-4 mr-2" />,
        onClick: openScan
      },
      ...(canCreate ? [{
        label: 'Re-Scan',
        icon: <RefreshCw className="w-4 h-4 mr-2" />,
        onClick: () => handleRescan(scan)
      }] : []),
      ...downloadReportAction,
      ...lifecycleActions
    ];
    // Format tag only for manual uploads — GitHub scans always produce an ORT result.
    const resultFormatBadge = getScanOrigin(scan) === 'manual' ? getResultFormatBadge(scan.resultformat) : null;


    const gridProps = {
      title: `${scan.application_name} | ${scan.project_name} # ${scan.version ?? ""}`,
      //description: scan.description,
      titleText: `Scan Code: ${scan.id} | Version: ${scan.scanparameters?.version || 'N/A'}`,
      description: `${scan.code}`,
      badges: [
        {
          label: scan.status,
          className: getStatusColor(scan.status)
        },
        {
          label: statusLabels[scan.scanstatus] || scan.scanstatus,
          className: getStatusColor(scan.scanstatus)
        },
        getScanOrigin(scan) === 'github'
          ? { label: 'GitHub', className: 'bg-gray-900 text-white border-gray-700' }
          : { label: 'Manual', className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700' },
        ...(resultFormatBadge ? [resultFormatBadge] : []),
      ].filter(Boolean),
      metrics: [
        // {
        //   label: `${scan.application_name}`,
        //   value: '',
        //   icon: <Package className="w-4 h-4 mr-1" />
        // },
        // {
        //   label: `${scan.project_name}`,
        //   value: '',
        //   icon: <FolderOpen className="w-4 h-4 mr-1" />
        // },
        {
          label: `${scan.componentsfound || 0} Components`,
          value: '',
          icon: <Component className="w-4 h-4 mr-1" />
        },
        // {
        //   label: `${scan.filesscanned || 0} Files`,
        //   value: '',
        //   icon: <FileText className="w-4 h-4 mr-1" />
        // },
        {
          label: `${scan.creator_firstname}`,
          value: '',
          icon: <User className="w-4 h-4 mr-1" />
        },
        {
          label: `${scan.lastactivityon ?
            new Date(scan.lastactivityon).toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
            : 'No activity'}`,
          value: '',
          icon: <Calendar className="w-4 h-4 mr-1" />
        },
      ],
      vulnerabilities: {
        critical: scan.critical_vulnerabilities || 0,
        high: scan.high_vulnerabilities || 0,
        medium: scan.medium_vulnerabilities || 0,
        low: scan.low_vulnerabilities || 0
      },
      licenseIssues: {
        critical: scan.critical_license_issues || 0,
        high: scan.high_license_issues || 0,
        medium: scan.medium_license_issues || 0,
        low: scan.low_license_issues || 0
      },

      riskScores: {
        overallScore: scan.overallscore || 0,
        vulnerabilityScore: scan.vulnerabilityscore || Math.min(10, scan.vulnerabilitiesfound || 0),
        licenseScore: scan.licensescore || 0,
        operationalRisk: scan.operationalriskscore || 0,
        technicalDebt: scan.technicaldebtscore || 0,
        businessRisk: scan.businessriskscore || 0,
        criticalVulnerabilities: scan.critical_vulnerabilities || 0,
        highVulnerabilities: scan.high_vulnerabilities || 0,
        mediumVulnerabilities: scan.medium_vulnerabilities || 0,
        lowVulnerabilities: scan.low_vulnerabilities || 0,
        criticalLicenseIssues: scan.critical_license_issues || 0,
        highLicenseIssues: scan.high_license_issues || 0,
        mediumLicenseIssues: scan.medium_license_issues || 0,
        lowLicenseIssues: scan.low_license_issues || 0,
        scanDuration: scan.scan_duration_minutes || 0,
        scanQuality: scan.scan_quality_score || 0
      },
      actions,
      onClick: openScan,
      href: scanHref
    };
    const listProps = {

      title: `${scan.application_name} | ${scan.project_name} # ${scan.version ?? ""}`,
      //description: scan.description,
      titleText: `Scan Code: ${scan.id} | Version: ${scan.scanparameters?.version || 'N/A'}`,
      description: `${scan.code}`,
      badges: [
        {
          label: scan.status,
          className: getStatusColor(scan.status)
        },
        {
          label: statusLabels[scan.scanstatus] || scan.scanstatus,
          className: getStatusColor(scan.scanstatus)
        },
        getScanOrigin(scan) === 'github'
          ? { label: 'GitHub', className: 'bg-gray-900 text-white border-gray-700' }
          : { label: 'Manual', className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700' },
        ...(resultFormatBadge ? [resultFormatBadge] : []),
      ].filter(Boolean),
      metrics: [
        {
          label: `${scan.componentsfound || 0}`,
          value: 'components',
          icon: <Component className="w-4 h-4 mr-1" />
        },
        // {
        //   label: `${scan.filesscanned || 0}`,
        //   value: 'files',
        //   icon: <FileText className="w-4 h-4 mr-1" />
        // },

        {
          label: `${scan.creator_firstname}`,
          value: '',
          icon: <User className="w-4 h-4 mr-1" />
        },
        {
          label: `${scan.lastactivityon ?
            new Date(scan.lastactivityon).toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
            : 'No activity'}`,
          value: '',
          icon: <Calendar className="w-4 h-4 mr-1" />
        }
      ],
      vulnerabilities: {
        critical: scan.critical_vulnerabilities || 0,
        high: scan.high_vulnerabilities || 0,
        medium: scan.medium_vulnerabilities || 0,
        low: scan.low_vulnerabilities || 0
      },
      licenseIssues: {
        critical: scan.critical_license_issues || 0,
        high: scan.high_license_issues || 0,
        medium: scan.medium_license_issues || 0,
        low: scan.low_license_issues || 0
      },
      riskScores: {
        overallScore: scan.overallscore || 0,
        vulnerabilityScore: scan.vulnerabilityscore || Math.min(10, scan.vulnerabilitiesfound || 0),
        licenseScore: scan.licensescore || 0,
        operationalRisk: scan.operationalriskscore || 0,
        technicalDebt: scan.technicaldebtscore || 0,
        businessRisk: scan.businessriskscore || 0,
        criticalVulnerabilities: scan.critical_vulnerabilities || 0,
        highVulnerabilities: scan.high_vulnerabilities || 0,
        mediumVulnerabilities: scan.medium_vulnerabilities || 0,
        lowVulnerabilities: scan.low_vulnerabilities || 0,
        criticalLicenseIssues: scan.critical_license_issues || 0,
        highLicenseIssues: scan.high_license_issues || 0,
        mediumLicenseIssues: scan.medium_license_issues || 0,
        lowLicenseIssues: scan.low_license_issues || 0,
        scanDuration: scan.scan_duration_minutes || 0,
        scanQuality: scan.scan_quality_score || 0
      },

      actions,
      onClick: openScan,
      href: scanHref
    }
    return (
      <UnifiedCard
        isGridLayout={isGridLayout}
        gridProps={gridProps}
        listProps={listProps}
        className={"gap-0"}
      >
        {/* {!isGridLayout && (
          <>
            <div className="flex items-center space-x-2 flex-shrink-0">

              <div className="text-center">
                <Badge variant="outline" className="bg-muted" title='Application Name'>
                  <Package className="w-4 h-4 mr-1" />
                  {scan.application_name}
                </Badge>
              </div>
              <div className="text-center">
                <Badge variant="outline" className="bg-muted" title='Project Name'>
                  <FolderOpen className="w-4 h-4 mr-1" />
                  {scan.project_name}
                </Badge>
              </div>
            </div>



          </>
        )} */}
      </UnifiedCard>
    );
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">Loading scans...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>


      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2 justify-center sm:justify-end">
          <Badge
            variant="secondary"
            className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-xs whitespace-nowrap"
            onClick={clearAllFilters}
            title="Click to clear all filters"
          >
            <span className="whitespace-nowrap">{allScans?.length || 0} Total</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${statusFilter === 'Active'
              ? 'bg-green-200 text-green-900 border-green-300 hover:bg-green-300 dark:bg-green-800 dark:text-green-100'
              : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
              }`}
            onClick={() => handleStatusFilter(statusFilter === 'Active' ? 'all' : 'Active')}
            title="Click to filter by Active status"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allScans?.filter((s: Scan) => s.isactive).length || 0}</span>
            <span className="whitespace-nowrap">Active</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${statusFilter === 'Archived'
              ? 'bg-yellow-200 text-yellow-900 border-yellow-300 hover:bg-yellow-300 dark:bg-yellow-800 dark:text-yellow-100'
              : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200'
              }`}
            onClick={() => handleStatusFilter(statusFilter === 'Archived' ? 'all' : 'Archived')}
            title="Click to filter by Archived status"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allScans?.filter((s: Scan) => !s.isactive).length || 0}</span>
            <span className="whitespace-nowrap">Archived</span>
          </Badge>

          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${progressFilter === ScanStatus.COMPLETED
              ? 'bg-green-200 text-green-900 border-green-300 hover:bg-green-300 dark:bg-green-800 dark:text-green-100'
              : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
              }`}
            onClick={() => handleProgressFilter(progressFilter === ScanStatus.COMPLETED ? 'all' : ScanStatus.COMPLETED)}
            title="Click to filter by Completed status"
          >
           
            <span className="text-xs sm:text-sm whitespace-nowrap">{allScans?.filter((s: Scan) => s.scanstatus === ScanStatus.COMPLETED).length || 0}</span>
            <span className="whitespace-nowrap">Completed</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${progressFilter === ScanStatus.RUNNING
              ? 'bg-blue-200 text-blue-900 border-blue-300 hover:bg-blue-300 dark:bg-blue-800 dark:text-blue-100'
              : 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200'
              }`}
            onClick={() => handleProgressFilter(progressFilter === ScanStatus.RUNNING ? 'all' : ScanStatus.RUNNING)}
            title="Click to filter by Running status"
          >
           
            <span className="text-xs sm:text-sm whitespace-nowrap">{allScans?.filter((s: Scan) => s.scanstatus === ScanStatus.RUNNING).length || 0}</span>
            <span className="whitespace-nowrap">Running</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${progressFilter === 'failed'
              ? 'bg-red-200 text-red-900 border-red-300 hover:bg-red-300 dark:bg-red-800 dark:text-red-100'
              : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200'
              }`}
            onClick={() => handleProgressFilter(progressFilter === 'failed' ? 'all' : 'failed')}
            title="Click to filter by Failed status"
          >
           
            <span className="text-xs sm:text-sm whitespace-nowrap">{allScans?.filter((s: Scan) => s.scanstatus === 'failed').length || 0}</span>
            <span className="whitespace-nowrap">Failed</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${progressFilter === 'cancelled'
              ? 'bg-gray-200 text-gray-900 border-gray-300 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-100'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-900 dark:text-gray-200'
              }`}
            onClick={() => handleProgressFilter(progressFilter === 'cancelled' ? 'all' : 'cancelled')}
            title="Click to filter by Cancelled status"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allScans?.filter((s: Scan) => s.scanstatus === 'cancelled').length || 0}</span>
            <span className="whitespace-nowrap">Cancelled</span>
          </Badge>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 flex-1">
            <div className="relative w-full xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground z-10" />
              <Input
                placeholder="Search scans..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 w-full bg-white rounded-lg shadow-sm border-border text-sm"
                maxLength={200}
              />
            </div>
            {(!projectId && !applicationId) && (
              <div className="relative w-full xs:flex-1 sm:max-w-xs">
                <MultiSelect
                  options={applicationOptions}
                  value={selectedApplications}
                  onChange={handleApplicationFilterChange}
                  placeholder="Filter by applications..."
                  searchPlaceholder="Search applications..."
                  loading={applicationsLoading}
                  maxDisplayed={2}
                  className="bg-background rounded-lg shadow-sm border-border"
                />
             
              </div>
            )}
            {!projectId && (
              <div className="relative w-full xs:flex-1 sm:max-w-xs">
                <MultiSelect
                  options={projectOptions}
                  value={selectedProjects}
                  onChange={handleProjectFilterChange}
                  placeholder={
                    selectedApplications.length > 0
                      ? `Filter projects (${projectOptions.length} available)...`
                      : "Filter by projects..."
                  }
                  searchPlaceholder="Search projects..."
                  loading={projectsLoading}
                  maxDisplayed={2}
                  className="bg-background rounded-lg shadow-sm border-border"
                />
              
              </div>
            )}
            <div className="relative w-full flex flex-1 gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`bg-white rounded-lg shadow-sm border-border ${statusFilter !== 'all' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''
                      }`}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Status: {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    {statusFilter !== 'all' && <X className="w-3 h-3 ml-1" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleStatusFilter('all')}>All Status</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusFilter('Active')}>Active</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusFilter('Archived')}>Archived</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`bg-white rounded-lg shadow-sm border-border ${sortBy !== 'createdon' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''
                      }`}
                  >
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Sort: {getSortDisplayTitle(sortBy)}
                    {sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleSort('createdon')}>Created Date</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('startedat')}>Start Date</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('completedat')}>Completion Date</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('progresspercentage')}>Progress</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('filesscanned')}>Files Scanned</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('componentsfound')}>Components Found</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('vulnerabilitiesfound')}>Vulnerabilities Found</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('licenseissuesfound')}>License Issues</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('overallscore')}>Overall Score</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Clear All</span>
                  <span className="sm:hidden">Clear</span>
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4 xl:shrink-0">

            <div className="flex flex-wrap items-center gap-2 shrink-0">

            </div>
          </div>
        </div>

      </div>


      <div className={isGridLayout
        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        : "space-y-4"
      }>
        {paginatedScans.map((scan: Scan) => (
          <ScanCard
            key={scan.id}
            scan={scan}
          />
        ))}
      </div>

      {paginatedScans.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {hasActiveFilters
              ? "No scans found matching your filters"
              : "No scans found"
            }
          </p>
          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={clearAllFilters}
              className="mt-4"
            >
              Clear all filters
            </Button>
          )}
        </div>
      )}

      <Paginator
        totalItems={filteredScans.length}
        currentPage={currentPage}
        perPage={9}
        onPageChange={handlePageChange}
        className="mt-6"
      />


      <ArchiveScan
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        scan={selectedScan}
        onSuccess={handleModalSuccess}
      />

      <DeleteScan
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        scan={selectedScan}
        onSuccess={handleModalSuccess}
      />

      <RestoreScan
        isOpen={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        scan={selectedScan}
        onSuccess={handleModalSuccess}
      />

      <NewScan
        key={selectedScan?.id ?? 'new'}
        isOpen={rescanModalOpen}
        onClose={() => setRescanModalOpen(false)}
        onNavigate={onNavigate}
        workgroupId={workgroupId}
        projectId={selectedScan?.projectid}
        existingScan={selectedScan}
      />

      <UploadOrtFiles
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        scan={selectedScan}
      />
    </div>
  );
}